import { createElement, useCallback, useEffect, useMemo, useState } from 'react';
import { HOA_DON_BAN_HANG_TABLE_TABS, TABLE_PAGE_SIZE } from '../constants';
import type { HoaDonBanHang, HoaDonBanHangInput } from '@/types/schemas';
import { useForm, useSearchFieldStates, useSearchTable } from '../hooks';
import { initialSearchValues, SearchFormValues } from '../schema';
import { DATA_TABLE_COLUMNS } from '../cols-definition';
import { useAuth } from '@/contexts/auth-context';
import { fetchSoDuTonKho } from '../utils';
import { QUERY_KEYS } from '@/constants';
import { useCRUD } from '@/hooks';

export const useHoaDonBanHangTable = () => {
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const [searchParams, setSearchParams] = useState<SearchFormValues>(initialSearchValues);
  const { entity } = useAuth();
  const [detailRowsWithStock, setDetailRowsWithStock] = useState<any[]>([]);

  const {
    data,
    detail,
    fetchDetail,
    addItem,
    currentPage,
    deleteItem,
    refreshData,
    isLoading,
    isDetailLoading,
    handlePageChange,
    totalItems,
    updateItem,
    sumTotal,
    searchData,
    isFiltered
  } = useCRUD<HoaDonBanHang, HoaDonBanHangInput>({
    endpoint: QUERY_KEYS.HOA_DON_BAN_HANG,
    pageSize: TABLE_PAGE_SIZE,
    sumField: 't_tt_nt',
    useFilter: true
  });

  const {
    showForm,
    showDelete,
    formMode,
    isCopyMode,
    showCantEdit,
    setShowCantEdit,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    clearSelection,
    selectedObj,
    selectedRowIndex,
    handleRowClick,
    showBulkDelete,
    handleCloseBulkDelete,
    handleImportExcel
  } = useForm(fetchDetail);

  useEffect(() => {
    let isMounted = true;
    const fetchStock = async () => {
      const chiTiet = (detail as BanHangRecord)?.chi_tiet || [];
      if (!chiTiet.length || !entity?.slug || !(detail as BanHangRecord)?.ngay_ct) {
        setDetailRowsWithStock(chiTiet);
        return;
      }

      try {
        const items = chiTiet
          .map((row: any) => ({
            ma_vt: row.ma_vt_data?.uuid || row.ma_vt || '',
            dvt: row.dvt_data?.uuid || row.dvt || row.ma_vt_data?.dvt_nl || row.ma_vt_data?.dvt || '',
            ma_kho: row.ma_kho_data?.uuid || row.ma_kho || ''
          }))
          .filter((item: any) => item.ma_vt);

        if (items.length === 0) {
          setDetailRowsWithStock(chiTiet);
          return;
        }

        const response = await fetchSoDuTonKho(entity.slug, String((detail as BanHangRecord).ngay_ct), items);

        if (isMounted) {
          const newRows = chiTiet.map((row: any) => {
            const vtUuid = row.ma_vt_data?.uuid || row.ma_vt;
            const khoUuid = row.ma_kho_data?.uuid || row.ma_kho;
            const found = response.results.find(r => r.ma_vt === vtUuid && (r.ma_kho === khoUuid || !r.ma_kho));
            if (found) {
              return { ...row, sl_ton: Number(found.so_du) };
            }
            return row;
          });
          setDetailRowsWithStock(newRows);
        }
      } catch (error) {
        console.error('Failed to fetch stock for detail table', error);
        if (isMounted) {
          setDetailRowsWithStock(chiTiet);
        }
      }
    };
    fetchStock();
    return () => {
      isMounted = false;
    };
  }, [detail, entity?.slug]);

  const { state, actions } = useSearchFieldStates();
  const {
    handleSearchChange,
    onPageChange,
    handleInitialSearch,
    handleRefreshClick,
    isFiltering,
    searchState,
    hasFilterParams
  } = useSearchTable({
    searchData,
    searchParams,
    setSearchParams,
    isFiltered,
    handlePageChange
  });

  const refreshVisibleData = useCallback(async () => {
    await refreshData();

    if (hasFilterParams) {
      await handleRefreshClick();
    }
  }, [handleRefreshClick, hasFilterParams, refreshData]);

  const handleSearchOpen = useCallback(() => {
    setShowSearchDialog(true);
  }, []);

  const handleSearchClose = useCallback(() => {
    setShowSearchDialog(false);
  }, []);

  const handleSearchSubmit = useCallback(
    async (filters: SearchFormValues) => {
      await handleInitialSearch(filters);
      setShowSearchDialog(false);
    },
    [handleInitialSearch]
  );

  const handleRefresh = useCallback(async () => {
    await refreshVisibleData();
    await fetchDetail(selectedObj?.uuid);
  }, [fetchDetail, refreshVisibleData, selectedObj?.uuid]);

  const handleCantEditClose = useCallback(() => {
    setShowCantEdit(false);
  }, [setShowCantEdit]);

  const displayData = hasFilterParams ? searchState.searchResults : data;
  const displayIsLoading = isLoading || isFiltering;
  const resolvedSumTotal = hasFilterParams ? (searchState.totalRow?.t_tt_nt ?? sumTotal) : sumTotal;
  const columns = useMemo(
    () => DATA_TABLE_COLUMNS(handleViewClick, resolvedSumTotal, displayIsLoading),
    [displayIsLoading, handleViewClick, resolvedSumTotal]
  );
  const tables = useMemo(
    () =>
      HOA_DON_BAN_HANG_TABLE_TABS.map(tab => ({
        name: tab.name,
        rows: tab.status ? displayData.filter(row => String(row?.status ?? '') === tab.status) : displayData,
        columns,
        icon: tab.color
          ? createElement('div', {
              className: 'mr-2 size-[7px] rounded-full',
              style: { backgroundColor: tab.color }
            })
          : undefined
      })),
    [columns, displayData]
  );

  return {
    crud: {
      detail,
      isDetailLoading,
      fetchDetail,
      addItem,
      deleteItem,
      refreshData: refreshVisibleData,
      updateItem,
      detailRowsWithStock
    },
    selection: {
      selectedObj,
      selectedRowIndex,
      clearSelection
    },
    dialogs: {
      showForm,
      showDelete,
      showCantEdit,
      showBulkDelete,
      isCopyMode
    },
    form: {
      formMode,
      handleCloseForm,
      handleCloseDelete,
      handleAddClick,
      handleEditClick,
      handleViewClick,
      handleDeleteClick,
      handleCopyClick,
      handleRowClick,
      handleCloseBulkDelete,
      handleCantEditClose,
      handleImportExcel
    },
    searchDialog: {
      open: showSearchDialog,
      searchParams,
      searchFieldState: state,
      searchFieldActions: actions,
      onOpen: handleSearchOpen,
      onClose: handleSearchClose,
      onSubmit: handleSearchSubmit
    },
    table: {
      tables,
      columns,
      displayData,
      displayIsLoading,
      currentPage: hasFilterParams ? searchState.currentPage : currentPage,
      totalItems: hasFilterParams ? searchState.totalCount : totalItems,
      onPageChange,
      onSearchChange: handleSearchChange,
      onRefresh: handleRefresh
    }
  };
};
